import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { IClient } from '~/types/client';
import { formatLongDate } from '~/utils/trialPricing';

import styles from './HomeUnsubscribedModal.module.css';

function IconPause() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1.2" />
            <rect x="14" y="5" width="4" height="14" rx="1.2" />
        </svg>
    );
}
function IconUser() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    );
}

/**
 * Calque « Abonnement en pause » sur le lanceur de jeux (pendant la pause,
 * l'accès aux jeux est coupé). Volontairement DISTINCT de
 * HomeUnsubscribedModal : son bouton « Réactiver » ouvre un nouveau Checkout
 * → 2e abonnement → double prélèvement.
 *
 * Pas de « Reprendre maintenant » ici : c'est l'écran de la TV du lieu, à la
 * portée de n'importe qui (personnel, clients). La reprise, qui déclenche un
 * prélèvement, se fait depuis Mon compte (admins seulement).
 *
 * Même calque `absolute` que HomeUnsubscribedModal (gotcha #143) : la barre du
 * haut (Mon compte, factures, changement d'établissement) reste utilisable.
 */
export function HomePausedModal({ client }: { client: IClient }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const resumesAt = client.pause?.resumesAt;

    return (
        // PAS `aria-modal` : la barre du haut reste volontairement utilisable (cf HomeUnsubscribedModal).
        <div className={styles.backdrop} role="dialog" aria-labelledby="paused-title">
            <div className={styles.card}>
                <div className={styles.iconWrap} style={{ color: 'var(--blue)', background: 'var(--blue-soft)' }}>
                    <IconPause />
                </div>
                <h2 className={styles.title} id="paused-title">
                    {t('home.paused.title')}
                </h2>
                <p className={styles.desc}>
                    {resumesAt
                        ? t('home.paused.desc', { date: formatLongDate(new Date(resumesAt), i18n.language) })
                        : t('home.paused.descNoEnd')}
                </p>
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.secondaryCta}
                        onClick={() => navigate('/account?tab=subscription', { state: { clientID: client.id } })}
                    >
                        <IconUser />
                        {t('home.paused.manage')}
                    </button>
                </div>
            </div>
        </div>
    );
}
